const AUTH_RESUME_KEY = "caas.authResume";
const AUTH_ERROR_KEY = "error";
const LEGACY_UNAUTH_URL_KEY = "unauthUrl";
const EXPLICIT_LOGOUT_KEY = "caas.explicitLogout";
const PREFERRED_LANDING_KEY = "caas.preferredLandingPath";
const ROUTE_LOGIN_HINTS_KEY = "caas.routeLoginHints";
const ACTIVE_AUTH_APPLICATION_ID_KEY = "caas.activeAuthApplicationId";
const AUTH_TOKEN_KEYS = ["BearerToken", "RefreshToken", "TokenExpiry"];
const AUTH_SESSION_KEYS = [
  ...AUTH_TOKEN_KEYS,
  ACTIVE_AUTH_APPLICATION_ID_KEY,
  AUTH_RESUME_KEY,
  AUTH_ERROR_KEY,
  LEGACY_UNAUTH_URL_KEY,
  EXPLICIT_LOGOUT_KEY,
  "authUser",
];
const VERIFY_CONTEXT_KEYS = ["email", "ubo_id", "form_id"];
const BUSINESS_PLUS_LOGIN_PATH = "/business-plus/login";
const BUSINESS_PRIME_PLUS_LOGIN_PATH = "/business-prime-plus/login";
const DEFAULT_LANDING_PATH = "/individual-plus";

export const DEFAULT_UNAUTHORIZED_MESSAGE =
  "Your session expired. Please log in again to continue.";
export const INVALID_VERIFY_LINK_MESSAGE =
  "This verification link is incomplete or invalid. Please reopen the original invitation link.";

const parseJson = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to parse auth resume state", error);
    return null;
  }
};

const writeJson = (key, value) => {
  if (!value) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, JSON.stringify(value));
};

const readJson = (key) => parseJson(localStorage.getItem(key));

const removeStorageKeys = (storage, keys = []) => {
  if (!storage) {
    return;
  }

  try {
    keys.forEach((key) => storage.removeItem(key));
  } catch (error) {
    console.error("Failed to clear auth storage", error);
  }
};

export const clearSessionAuthStorage = () => {
  if (typeof window === "undefined") {
    return;
  }

  removeStorageKeys(window.sessionStorage, AUTH_SESSION_KEYS);
};

const normalizePath = (value = "") => {
  if (!value) {
    return "";
  }

  return value.startsWith("/") ? value : `/${value}`;
};

const splitPathAndSearch = (value = "") => {
  const stringValue = `${value || ""}`;
  const queryIndex = stringValue.indexOf("?");

  if (queryIndex === -1) {
    return {
      pathname: normalizePath(stringValue),
      search: "",
    };
  }

  return {
    pathname: normalizePath(stringValue.slice(0, queryIndex)),
    search: stringValue.slice(queryIndex),
  };
};

const buildPathWithSearch = (pathname = "", search = "") =>
  `${normalizePath(pathname)}${search || ""}`;

const BUSINESS_PLUS_CART_ROUTE = /^\/business-plus\/cart\/[^/]+(?:\/[^/]+)?$/;
const PERIODIC_REVIEW_ROUTE = /^\/periodic-review-(individual-plus|individual-prime|business-plus|business-prime)\/[^/]+$/;
const COMPLETE_APPLICATION_ROUTE = /^\/complete-application\/([^/]+)$/;
const COMPLETE_APPLICATION_DASHBOARD_ROUTE = /^\/complete-application\/([^/]+)\/dashboard$/;
const APPLICATION_DETAIL_ROUTES = [
  /^\/business-plus\/[^/]+$/,
  /^\/business-prime\/[^/]+$/,
  /^\/business-prime-plus\/[^/]+$/,
  /^\/business-v2\/[^/]+$/,
  /^\/individual-plus\/[^/]+$/,
];

const isApplicationRoutePath = (pathname = "") =>
  BUSINESS_PLUS_CART_ROUTE.test(pathname) ||
  PERIODIC_REVIEW_ROUTE.test(pathname) ||
  APPLICATION_DETAIL_ROUTES.some((route) => route.test(pathname));

const isPreferredLandingPath = (pathname = "") =>
  isApplicationRoutePath(pathname) &&
  pathname !== BUSINESS_PLUS_LOGIN_PATH &&
  pathname !== "/business-prime/login" &&
  pathname !== "/business-prime-plus/login" &&
  pathname !== "/individual-plus/login";

const extractApplicationId = (pathname = "") => {
  if (BUSINESS_PLUS_CART_ROUTE.test(pathname)) {
    return pathname.split("/")[3] || "";
  }

  return isApplicationRoutePath(pathname)
    ? pathname.split("/").filter(Boolean).at(-1) || ""
    : "";
};

const extractApplicationIdFromPath = (path = "") => {
  const { pathname } = splitPathAndSearch(path);
  return extractApplicationId(pathname);
};

const getRouteLoginHints = () => readJson(ROUTE_LOGIN_HINTS_KEY) || {};

const writeRouteLoginHints = (hints = {}) => {
  if (!Object.keys(hints).length) {
    localStorage.removeItem(ROUTE_LOGIN_HINTS_KEY);
    return;
  }

  writeJson(ROUTE_LOGIN_HINTS_KEY, hints);
};

const getRouteHintForApplication = (applicationId = "") => {
  if (!applicationId) {
    return null;
  }

  const hints = getRouteLoginHints();
  return hints?.[applicationId] || null;
};

const decodePathSegment = (value = "") => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const getBusinessPlusLoginPath = (pathname = "") => {
  if (/^\/business-plus\/cart\/[^/]+\/[^/]+$/.test(pathname)) {
    return BUSINESS_PRIME_PLUS_LOGIN_PATH;
  }

  const hintedLoginPath = getRouteHintForApplication(extractApplicationId(pathname))?.loginPath;
  return hintedLoginPath || BUSINESS_PLUS_LOGIN_PATH;
};

const getAuthRouteConfig = (pathname = "") => {
  const completeApplicationDashboardMatch = pathname.match(COMPLETE_APPLICATION_DASHBOARD_ROUTE);
  if (completeApplicationDashboardMatch) {
    const uboId = decodePathSegment(completeApplicationDashboardMatch[1] || "");
    const loginPath = `/complete-application/${uboId}`;
    return {
      product: "complete-application",
      loginPath,
      shouldResume: true,
      formType: "UBO_KYC_LOGIN",
      verifyContext: {
        ubo_id: uboId,
      },
    };
  }

  const completeApplicationMatch = pathname.match(COMPLETE_APPLICATION_ROUTE);
  if (completeApplicationMatch) {
    const uboId = decodePathSegment(completeApplicationMatch[1] || "");
    return {
      product: "complete-application",
      loginPath: `/complete-application/${uboId}`,
      shouldResume: false,
      formType: "UBO_KYC_LOGIN",
      verifyContext: {
        ubo_id: uboId,
      },
    };
  }

  if (pathname === "/verify-individual" || pathname === "/verify-individual/") {
    return {
      product: "verify-individual",
      loginPath: "/verify-individual",
      shouldResume: false,
      includeVerifyQuery: true,
    };
  }

  if (pathname === "/verify-individual/dashboard") {
    return {
      product: "verify-individual",
      loginPath: "/verify-individual",
      shouldResume: true,
      includeVerifyQuery: true,
      formType: "UBO_KYC_LOGIN",
    };
  }

  if (pathname === "/periodic-review/login") {
    return {
      product: "periodic-review",
      loginPath: "/periodic-review/login",
      shouldResume: false,
      formType: "EDD",
    };
  }

  if (
    /^\/periodic-review-(individual-plus|individual-prime|business-plus|business-prime)\/[^/]+$/.test(
      pathname
    )
  ) {
    return {
      product: "periodic-review",
      loginPath: "/periodic-review/login",
      shouldResume: true,
      formType: "EDD",
    };
  }

  if (pathname === "/business-plus/login") {
    return {
      product: "business-plus",
      loginPath: BUSINESS_PLUS_LOGIN_PATH,
      shouldResume: false,
      formType: "BUSINESS_PLUS",
    };
  }

  if (
    /^\/business-plus\/cart\/[^/]+(?:\/[^/]+)?$/.test(pathname) ||
    /^\/business-plus\/[^/]+$/.test(pathname)
  ) {
    const loginPath = getBusinessPlusLoginPath(pathname);
    return {
      product: loginPath === BUSINESS_PRIME_PLUS_LOGIN_PATH ? "business-prime" : "business-plus",
      loginPath,
      shouldResume: true,
      formType: "BUSINESS_PLUS",
    };
  }

  if (pathname === "/business-plus") {
    return {
      product: "business-plus",
      loginPath: BUSINESS_PLUS_LOGIN_PATH,
      shouldResume: false,
      formType: "BUSINESS_PLUS",
    };
  }

  if (pathname === "/business-prime/login") {
    return {
      product: "business-prime",
      loginPath: "/business-prime/login",
      shouldResume: false,
      formType: "BUSINESS_PRIME",
    };
  }

  if (pathname === "/business-prime-plus/login") {
    return {
      product: "business-prime",
      loginPath: "/business-prime-plus/login",
      shouldResume: false,
      formType: "BUSINESS_PLUS",
    };
  }

  if (/^\/business-prime\/[^/]+$/.test(pathname)) {
    return {
      product: "business-prime",
      loginPath: "/business-prime/login",
      shouldResume: true,
      formType: "BUSINESS_PRIME",
    };
  }

  if (pathname === "/business-prime") {
    return {
      product: "business-prime",
      loginPath: "/business-prime/login",
      shouldResume: false,
      formType: "BUSINESS_PRIME",
    };
  }

  if (pathname === "/individual-plus/login") {
    return {
      product: "individual-plus",
      loginPath: "/individual-plus/login",
      shouldResume: false,
      formType: "INDIVIDUAL_PLUS",
    };
  }

  if (/^\/individual-plus\/[^/]+$/.test(pathname)) {
    return {
      product: "individual-plus",
      loginPath: "/individual-plus/login",
      shouldResume: true,
      formType: "INDIVIDUAL_PLUS",
    };
  }

  if (pathname === "/individual-plus") {
    return {
      product: "individual-plus",
      loginPath: "/individual-plus/login",
      shouldResume: false,
      formType: "INDIVIDUAL_PLUS",
    };
  }

  if (/^\/(broker|brokers|platform)(?:\/|$)/.test(pathname)) {
    const loginPath = pathname.startsWith("/platform")
      ? "/platform"
      : pathname.startsWith("/brokers")
        ? "/brokers"
        : "/broker";
    return {
      product: "broker",
      loginPath,
      shouldResume: true,
      formType: "",
    };
  }

  return {
    product: "",
    loginPath: "/",
    shouldResume: false,
    formType: "",
  };
};

const normalizeEmail = (value = "") => {
  if (!value) {
    return "";
  }

  try {
    return decodeURIComponent(value).replace(/ /g, "+");
  } catch (error) {
    return value.replace(/ /g, "+");
  }
};

const buildVerifyIndividualQuery = (context = {}) => {
  const searchParams = new URLSearchParams();

  VERIFY_CONTEXT_KEYS.forEach((key) => {
    if (context?.[key]) {
      searchParams.set(key, context[key]);
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

export const readAuthResume = () => parseJson(localStorage.getItem(AUTH_RESUME_KEY));

export const writeAuthResume = (resume) => {
  if (!resume) {
    localStorage.removeItem(AUTH_RESUME_KEY);
    return;
  }

  writeJson(AUTH_RESUME_KEY, resume);
};

export const clearAuthResume = () => {
  localStorage.removeItem(AUTH_RESUME_KEY);
};

export const clearAuthTokens = () => {
  AUTH_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem(ACTIVE_AUTH_APPLICATION_ID_KEY);
  localStorage.removeItem("authUser");
  localStorage.removeItem("BrokerProfile");
  localStorage.removeItem("BrokerRefreshToken");
  clearSessionAuthStorage();
};

export const persistAuthTokens = ({
  bearerToken,
  refreshToken,
  expiry,
  activeApplicationId = "",
} = {}) => {
  clearAuthTokens();

  if (bearerToken) {
    localStorage.setItem("BearerToken", bearerToken);
  }

  if (refreshToken) {
    localStorage.setItem("RefreshToken", refreshToken);
  }

  if (expiry) {
    localStorage.setItem("TokenExpiry", expiry);
  }

  setActiveAuthApplicationId(activeApplicationId);
};

export const getActiveAuthApplicationId = () => localStorage.getItem(ACTIVE_AUTH_APPLICATION_ID_KEY) || "";

export const setActiveAuthApplicationId = (applicationId = "") => {
  if (applicationId) {
    localStorage.setItem(ACTIVE_AUTH_APPLICATION_ID_KEY, applicationId);
  } else {
    localStorage.removeItem(ACTIVE_AUTH_APPLICATION_ID_KEY);
  }
};

export const clearVerifyIndividualContext = () => {
  VERIFY_CONTEXT_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const clearStoredAuthMessage = () => {
  localStorage.removeItem(AUTH_ERROR_KEY);

  const storedResume = readAuthResume();
  if (!storedResume?.message) {
    return;
  }

  const nextResume = { ...storedResume };
  delete nextResume.message;
  writeAuthResume(nextResume);
};

export const getStoredAuthMessage = () => {
  const storedResume = readAuthResume();
  return storedResume?.message || localStorage.getItem(AUTH_ERROR_KEY) || "";
};

export const markExplicitLogoutLanding = () => {
  localStorage.setItem(EXPLICIT_LOGOUT_KEY, "1");
};

export const consumeExplicitLogoutLanding = () => {
  const wasExplicitLogout = localStorage.getItem(EXPLICIT_LOGOUT_KEY) === "1";
  if (wasExplicitLogout) {
    localStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  }
  return wasExplicitLogout;
};

export const setLegacyUnauthUrl = (value = "") => {
  const normalizedValue = value.replace(/^\//, "");

  if (!normalizedValue) {
    localStorage.removeItem(LEGACY_UNAUTH_URL_KEY);
    return;
  }

  localStorage.setItem(LEGACY_UNAUTH_URL_KEY, normalizedValue);
};

const buildVerifyContextFromSearch = (search = "") => {
  const searchParams = new URLSearchParams(search || "");

  return {
    email: normalizeEmail(searchParams.get("email") || ""),
    ubo_id: searchParams.get("ubo_id") || "",
    form_id: searchParams.get("form_id") || "",
  };
};

export const hasCompleteVerifyIndividualContext = (context = {}) =>
  Boolean(context?.email && context?.ubo_id && context?.form_id);

export const getVerifyIndividualContext = ({ search = "", allowStoredFallback = true } = {}) => {
  const queryContext = buildVerifyContextFromSearch(search);
  if (!allowStoredFallback) {
    return queryContext;
  }

  const storedResume = readAuthResume();

  return {
    email: queryContext.email || normalizeEmail(storedResume?.verifyContext?.email || localStorage.getItem("email") || ""),
    ubo_id: queryContext.ubo_id || storedResume?.verifyContext?.ubo_id || localStorage.getItem("ubo_id") || "",
    form_id: queryContext.form_id || storedResume?.verifyContext?.form_id || localStorage.getItem("form_id") || "",
  };
};

export const syncVerifyIndividualContext = (context = {}) => {
  const storedResume = readAuthResume();
  const keepVerifyResume =
    storedResume?.product === "verify-individual" &&
    typeof storedResume?.resumePath === "string" &&
    storedResume.resumePath.startsWith("/verify-individual");

  VERIFY_CONTEXT_KEYS.forEach((key) => {
    if (context?.[key]) {
      localStorage.setItem(key, context[key]);
    }
  });

  const nextResume = {
    reason: keepVerifyResume ? storedResume?.reason : undefined,
    product: "verify-individual",
    loginPath: "/verify-individual",
    redirectPath: `/verify-individual${buildVerifyIndividualQuery(context)}`,
    resumePath: keepVerifyResume ? storedResume?.resumePath : "",
    applicationId: keepVerifyResume ? storedResume?.applicationId : "",
    formType: "UBO_KYC_LOGIN",
    message: keepVerifyResume ? storedResume?.message : "",
    verifyContext: {
      ...(keepVerifyResume ? storedResume?.verifyContext || {} : {}),
      ...context,
    },
  };

  writeAuthResume(nextResume);
};

export const getAuthMessageFromResponse = (response) => {
  const rawMessage =
    response?.data?.customMessage ??
    response?.data?.message ??
    response?.message ??
    response?.data;

  if (typeof rawMessage === "string" && rawMessage.trim()) {
    return rawMessage;
  }

  return DEFAULT_UNAUTHORIZED_MESSAGE;
};

export const buildAuthResumeFromRoute = ({
  pathname = window.location.pathname,
  search = window.location.search,
  message = DEFAULT_UNAUTHORIZED_MESSAGE,
} = {}) => {
  const routeConfig = getAuthRouteConfig(pathname);
  const verifyContext = routeConfig.verifyContext || (routeConfig.includeVerifyQuery
    ? getVerifyIndividualContext({ search })
    : null);
  const verifyQuery =
    routeConfig.includeVerifyQuery && verifyContext
      ? buildVerifyIndividualQuery(verifyContext)
      : "";

  const loginPath = normalizePath(routeConfig.loginPath || "/");

  return {
    reason: "UNAUTHORIZED",
    product: routeConfig.product,
    loginPath,
    redirectPath: `${loginPath}${verifyQuery}`,
    resumePath: routeConfig.shouldResume ? `${pathname}${search || ""}` : "",
    applicationId: extractApplicationId(pathname),
    formType: routeConfig.formType,
    message,
    verifyContext,
  };
};

export const prepareProtectedRouteRedirect = ({
  pathname = window.location.pathname,
  search = window.location.search,
} = {}) => {
  const storedResume = readAuthResume();
  const nextResume = buildAuthResumeFromRoute({
    pathname,
    search,
    message: storedResume?.message || "",
  });

  writeAuthResume(nextResume);
  setLegacyUnauthUrl(nextResume.loginPath);
  return nextResume.redirectPath || nextResume.loginPath || "/";
};

const isJwtExpired = (token = "") => {
  try {
    const payload = `${token}`.split(".")[1];
    if (!payload) return false;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof claims?.exp === "number" && claims.exp * 1000 <= Date.now();
  } catch (e) {
    return false;
  }
};

export const forceLogoutToLogin = ({
  response,
  pathname = window.location.pathname,
  search = window.location.search,
  requestToken = "",
  redirect = (path) => window.location.assign(path),
} = {}) => {
  const message = getAuthMessageFromResponse(response);
  const nextResume = buildAuthResumeFromRoute({ pathname, search, message });
  const currentToken = localStorage.getItem("BearerToken") || "";
  const routeApplicationId = nextResume?.applicationId || "";
  const activeApplicationId = getActiveAuthApplicationId();
  const isStaleTokenFailure = Boolean(requestToken && currentToken && requestToken !== currentToken);
  const isInactiveApplicationFailure = Boolean(
    activeApplicationId &&
    routeApplicationId &&
    activeApplicationId !== routeApplicationId
  );
  const storedTokenExpired = isJwtExpired(currentToken);

  if (!storedTokenExpired && (isStaleTokenFailure || isInactiveApplicationFailure)) {
    redirect(nextResume.redirectPath || nextResume.loginPath || "/");
    return nextResume;
  }

  writeAuthResume(nextResume);
  localStorage.setItem(AUTH_ERROR_KEY, message);
  setLegacyUnauthUrl(nextResume.loginPath);
  clearAuthTokens();
  redirect(nextResume.redirectPath || nextResume.loginPath || "/");
};

export const getStoredAuthRedirectPath = () => {
  const storedResume = readAuthResume();
  if (storedResume?.redirectPath) {
    return storedResume.redirectPath;
  }

  const legacyTarget = localStorage.getItem(LEGACY_UNAUTH_URL_KEY);
  if (!legacyTarget || !localStorage.getItem(AUTH_ERROR_KEY)) {
    return "";
  }

  return normalizePath(legacyTarget);
};

const getPreferredLandingPathFromRoute = ({
  pathname = window.location.pathname,
  search = window.location.search,
} = {}) => {
  const normalizedPath = normalizePath(pathname);

  if (!normalizedPath || normalizedPath === "/" || normalizedPath === "/logout") {
    return "";
  }

  if (isPreferredLandingPath(normalizedPath)) {
    return buildPathWithSearch(normalizedPath, search);
  }

  if (normalizedPath === "/verify-individual/dashboard") {
    const verifyContext = getVerifyIndividualContext({
      search,
      allowStoredFallback: true,
    });

    if (!hasCompleteVerifyIndividualContext(verifyContext)) {
      return "";
    }

    return buildPathWithSearch(normalizedPath, search);
  }

  if (COMPLETE_APPLICATION_DASHBOARD_ROUTE.test(normalizedPath)) {
    return buildPathWithSearch(normalizedPath, search);
  }

  return "";
};

export const rememberPreferredLandingFromRoute = ({
  pathname = window.location.pathname,
  search = window.location.search,
} = {}) => {
  const preferredLandingPath = getPreferredLandingPathFromRoute({ pathname, search });
  if (!preferredLandingPath) {
    return "";
  }

  localStorage.setItem(PREFERRED_LANDING_KEY, preferredLandingPath);
  return preferredLandingPath;
};

export const getStoredPreferredLandingPath = () => {
  const storedPath = localStorage.getItem(PREFERRED_LANDING_KEY) || "";
  if (!storedPath) {
    return "";
  }

  const { pathname, search } = splitPathAndSearch(storedPath);

  if (isPreferredLandingPath(pathname)) {
    return buildPathWithSearch(pathname, search);
  }

  if (pathname === "/verify-individual/dashboard") {
    const verifyContext = getVerifyIndividualContext({
      allowStoredFallback: true,
    });

    if (hasCompleteVerifyIndividualContext(verifyContext)) {
      return buildPathWithSearch(pathname, search);
    }
  }

  if (COMPLETE_APPLICATION_DASHBOARD_ROUTE.test(pathname)) {
    return buildPathWithSearch(pathname, search);
  }

  localStorage.removeItem(PREFERRED_LANDING_KEY);
  return "";
};

export const getDefaultLandingPath = () =>
  getStoredAuthRedirectPath() || getStoredPreferredLandingPath() || DEFAULT_LANDING_PATH;

export const getRootRedirectPath = () => {
  consumeExplicitLogoutLanding();
  return getDefaultLandingPath();
};

export const rememberApplicationLoginPath = ({ applicationId = "", loginPath = "" } = {}) => {
  const normalizedLoginPath = normalizePath(loginPath);
  if (!applicationId || !normalizedLoginPath) {
    return;
  }

  const hints = getRouteLoginHints();
  hints[applicationId] = { loginPath: normalizedLoginPath };
  writeRouteLoginHints(hints);
};

export const rememberApplicationLoginPathForUrl = ({ url = "", loginPath = "" } = {}) => {
  if (!url) {
    return;
  }

  const [rawPathname = ""] = `${url}`.split(/[?#]/);
  const pathname = normalizePath(rawPathname);
  rememberApplicationLoginPath({
    applicationId: extractApplicationId(pathname),
    loginPath,
  });
};

const invalidateStoredApplicationResumeState = (resumePath = "") => {
  const resumeApplicationId = extractApplicationIdFromPath(resumePath);

  clearAuthResume();

  const storedPreferredLandingPath = localStorage.getItem(PREFERRED_LANDING_KEY) || "";
  if (storedPreferredLandingPath) {
    const preferredLandingApplicationId = extractApplicationIdFromPath(storedPreferredLandingPath);
    if (!resumeApplicationId || preferredLandingApplicationId === resumeApplicationId) {
      localStorage.removeItem(PREFERRED_LANDING_KEY);
    }
  }

  const storedFormId = localStorage.getItem("formId") || "";
  if (!resumeApplicationId || storedFormId === resumeApplicationId) {
    localStorage.removeItem("formId");
  }
};

export const getExpectedLoginPathForForm = (form, pathname = window.location.pathname) => {
  if (form?.type === "kyc") {
    if (form?.completeApplication && form?.unauthUrl) {
      return normalizePath(form.unauthUrl);
    }
    return "/verify-individual";
  }

  if (form?.formType === "EDD") {
    return "/periodic-review/login";
  }

  if (form?.path === "business-prime") {
    return "/business-prime/login";
  }

  if (form?.path === "individual-plus") {
    return "/individual-plus/login";
  }

  if (form?.path === "business-plus" && form?.isPrime) {
    return "/business-prime-plus/login";
  }

  if (form?.path === "business-plus") {
    return "/business-plus/login";
  }

  return pathname;
};

export const getResumePathForForm = (form, pathname = window.location.pathname) => {
  const storedResume = readAuthResume();
  if (!storedResume?.resumePath) {
    return "";
  }

  const expectedLoginPath = getExpectedLoginPathForForm(form, pathname);
  if (storedResume?.loginPath !== expectedLoginPath) {
    return "";
  }

  const resumePath = storedResume.resumePath;

  if (form?.type === "kyc") {
    if (form?.completeApplication) {
      return resumePath.startsWith("/complete-application/") ? resumePath : "";
    }
    return resumePath.startsWith("/verify-individual") ? resumePath : "";
  }

  if (form?.formType === "EDD") {
    return resumePath.startsWith("/periodic-review-") ? resumePath : "";
  }

  if (form?.path === "business-prime") {
    return resumePath.startsWith("/business-prime/") ? resumePath : "";
  }

  if (form?.path === "individual-plus") {
    return resumePath.startsWith("/individual-plus/") ? resumePath : "";
  }

  if (form?.path === "business-plus") {
    return resumePath.startsWith("/business-plus/") ? resumePath : "";
  }

  return resumePath;
};

export const getValidatedResumePathForApplication = ({
  form,
  applicationId = "",
  requireApplicationId = false,
} = {}) => {
  const resumePath = getResumePathForForm(form);
  if (!resumePath) {
    return "";
  }

  const resumeApplicationId = extractApplicationIdFromPath(resumePath);
  if (resumeApplicationId && !applicationId) {
    invalidateStoredApplicationResumeState(resumePath);
    return "";
  }

  if (requireApplicationId && (!applicationId || !resumeApplicationId)) {
    if (resumeApplicationId && !applicationId) {
      invalidateStoredApplicationResumeState(resumePath);
    }
    return "";
  }

  if (applicationId && resumeApplicationId && resumeApplicationId !== applicationId) {
    invalidateStoredApplicationResumeState(resumePath);
    return "";
  }

  return resumePath;
};
